import PreviewTableMaterialIdInput from "../../../../../BulkATCPopup/components/PreviewTable/PreviewTableMaterialIdInput";
import { forwardRef, type ReactNode, useMemo } from "react";
import { MaterialInput } from "../../../../../common/components/materialInput/MaterialInput";
import { Icon } from "../../../../../common/components/icon/Icon";
import { IconType } from "../../../../../common/components/icon/IconType";
import classes from "./SearchByInput.module.less";
import {
  MultilevelSelect,
  type MultilevelSelectItemItem,
} from "../../../../../common/components/Select/MultilevelSelect";
import classNames from "classnames";
import { useTranslation, type WithTranslation } from "react-i18next";
import { styled } from "@mui/material";

type PageVariant = "abmonitor" | "order" | "quote";
interface Props {
  className?: string;
  helperLabel?: ReactNode;

  value: SearchByData;
  onChange: (newData: SearchByData) => void;
  onBlur?: () => void;

  visibleOptions: QueryType[];
  searchDisabled?: boolean;
  disabled?: boolean;
  error?: boolean;
  pageVariant: PageVariant;
}

// Synchronize string values with ABMonitorReportExcelGenerator.java#getFilterType
export enum QueryType {
  PURCHASE_NO = "po",
  SALES_NO = "so",
  DESIGNATION = "designation",
  SAP_MATERIAL_NO = "sapmatno",
  CUSTOMER_MATERIAL_NO = "custalias",
  CUSTOMER_ORDER_NO = "custorder",
  INVOICE_NO = "invoiceno",
}

export interface SearchByData {
  type: QueryType;
  query: string;
}

export const SearchByInput = forwardRef<HTMLInputElement, Props>(
  (
    { className, helperLabel, value, onChange, visibleOptions, searchDisabled, disabled, error, onBlur, pageVariant },
    ref,
  ) => {
    const { t } = useTranslation();
    const options = useOptions(visibleOptions, pageVariant);
    const autoComplete = value.type === QueryType.DESIGNATION;
    const onChangeCb = (newData: Partial<SearchByData>) => onChange({ ...value, ...newData });

    return (
      <div className={classNames("d-flex", className)}>
        <div className={classes.textSection}>
          {autoComplete && (
            <ProductAutoComplete
              onChange={(data) => {
                if (!data?.designation) {
                  return;
                }
                onChangeCb({
                  query: data.designation,
                });
              }}
              value={value.query}
              label={t("orderListPage.searchBtn")}
              data-test-id={"search-by-input-query-input"}
              error={error}
              helperText={helperLabel}
              onBlur={onBlur}
              ref={ref}
              shrink={true}
            />
          )}
          {!autoComplete && (
            <MaterialInput
              disabled={disabled}
              label={t("orderListPage.searchBtn")}
              id={"search"}
              shrink={true}
              helperLabel={helperLabel}
              data-test-id={"search-by-input-query-input"}
              inputRef={ref}
              error={error}
              value={value.query}
              onBlur={onBlur}
              onChange={(e) => onChangeCb({ query: e.target.value })}
              endAdornment={
                <button type="submit" disabled={disabled || searchDisabled} className={classes.searchIcon}>
                  <Icon type={IconType.Search} />
                </button>
              }
            />
          )}
        </div>
        <div className={classes.dropdownSection}>
          <MultilevelSelect
            label={t("orderList.sortBy")}
            onChange={(eventValue) => {
              onChangeCb({ type: eventValue.target.value });
              onBlur?.();
            }}
            value={value.type}
            list={options}
            data-test-id={"search-by-input-by-dropdown"}
            disabledLabel={true}
          />
        </div>
      </div>
    );
  },
);

const TRANSLATION_KEYS = {
  abmonitor: {
    [QueryType.PURCHASE_NO]: "searchByInput.queryType.po.openorders",
    [QueryType.SALES_NO]: "searchByInput.queryType.so.openorders",
    [QueryType.DESIGNATION]: "searchByInput.queryType.part",
    [QueryType.SAP_MATERIAL_NO]: "searchByInput.queryType.sap",
    [QueryType.CUSTOMER_MATERIAL_NO]: "searchByInput.queryType.customMaterialNumber",
    [QueryType.CUSTOMER_ORDER_NO]: "searchByInput.queryType.lineCustomerOrderId",
    [QueryType.INVOICE_NO]: "searchByInput.queryType.invoiceNumber",
  },
  order: {
    [QueryType.PURCHASE_NO]: "searchByInput.queryType.po.orderlist",
    [QueryType.SALES_NO]: "searchByInput.queryType.so.orderlist",
    [QueryType.DESIGNATION]: "searchByInput.queryType.part",
    [QueryType.SAP_MATERIAL_NO]: "searchByInput.queryType.sap",
    [QueryType.CUSTOMER_MATERIAL_NO]: "searchByInput.queryType.customMaterialNumber",
    [QueryType.CUSTOMER_ORDER_NO]: "searchByInput.queryType.lineCustomerOrderId",
    [QueryType.INVOICE_NO]: "searchByInput.queryType.invoiceNumber",
  },
  quote: {
    [QueryType.PURCHASE_NO]: "searchByInput.queryType.po.quotelist",
    [QueryType.SALES_NO]: "searchByInput.queryType.so.quotelist",
    [QueryType.DESIGNATION]: "searchByInput.queryType.part",
    [QueryType.SAP_MATERIAL_NO]: "searchByInput.queryType.sap",
    [QueryType.CUSTOMER_MATERIAL_NO]: "searchByInput.queryType.customMaterialNumber",
    [QueryType.CUSTOMER_ORDER_NO]: "searchByInput.queryType.lineCustomerOrderId",
    [QueryType.INVOICE_NO]: "searchByInput.queryType.invoiceNumber",
  },
};

const useOptions = (visibleOptions: QueryType[], pageVariant: PageVariant) => {
  const { t } = useTranslation();
  const translateItem = (item: MultilevelSelectItemItem) => ({ ...item, label: t(item.label) });

  return useMemo(() => {
    const options = getOptionsRaw(pageVariant)
      .map((category) => ({
        ...category,
        children: category
          .children!.filter((child) => visibleOptions.includes(child.value as QueryType))
          .map(translateItem),
      }))
      .filter((category) => category.children)
      .map(translateItem);
    if (options.length === 1) {
      return options[0].children!;
    }
    return options;
  }, [visibleOptions]);
};

const getOptionsRaw = (pageVariant: PageVariant): MultilevelSelectItemItem[] => [
  {
    label: "searchByInput.queryType.oh",
    value: "group1",
    title: true,
    children: [
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.PURCHASE_NO],
        value: QueryType.PURCHASE_NO,
        parentValue: "group1",
      },
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.SALES_NO],
        value: QueryType.SALES_NO,
        parentValue: "group1",
      },
    ],
  },
  {
    label: "searchByInput.queryType.ol",
    value: "group2",
    title: true,
    children: [
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.CUSTOMER_ORDER_NO],
        value: QueryType.CUSTOMER_ORDER_NO,
        parentValue: "group2",
      },
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.DESIGNATION],
        value: QueryType.DESIGNATION,
        parentValue: "group2",
      },
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.SAP_MATERIAL_NO],
        value: QueryType.SAP_MATERIAL_NO,
        parentValue: "group2",
      },
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.CUSTOMER_MATERIAL_NO],
        value: QueryType.CUSTOMER_MATERIAL_NO,
        parentValue: "group2",
      },
      {
        label: TRANSLATION_KEYS[pageVariant][QueryType.INVOICE_NO],
        value: QueryType.INVOICE_NO,
        parentValue: "group2",
      },
    ],
  },
];

const materialNumberRegexp = /^\d{0,15}$/;
export const makeSearchByValidator =
  (t: WithTranslation["t"], pageVariant: PageVariant) =>
  (data: SearchByData): boolean | string => {
    const tooLong = (fieldKey: string) =>
      t("checkoutPage.addressModal.error.isTooLongPart", { fieldname: "" + t(fieldKey) });
    switch (data.type) {
      case QueryType.PURCHASE_NO: {
        return data.query.trim().length <= 35 || tooLong(TRANSLATION_KEYS[pageVariant][QueryType.PURCHASE_NO]);
      }
      case QueryType.SALES_NO: {
        return data.query.trim().length <= 10 || tooLong(TRANSLATION_KEYS[pageVariant][QueryType.SALES_NO]);
      }
      case QueryType.DESIGNATION: {
        return data.query.trim().length <= 30 || tooLong(TRANSLATION_KEYS[pageVariant][QueryType.DESIGNATION]);
      }
      case QueryType.SAP_MATERIAL_NO: {
        return materialNumberRegexp.test(data.query.trim()) || (t("contractOverview.materialNumberWarning") as string);
      }
    }
    return true;
  };

const ProductAutoComplete = styled(PreviewTableMaterialIdInput)`
  .MuiOutlinedInput-input {
    width: auto !important;
  }
`;
